import React from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Loader2, 
  CheckCircle2, 
  AlertTriangle, 
  Edit3, 
  Save, 
  X, 
  UtensilsCrossed, 
  ChefHat,
  Heart,
  Shield,
  Sparkles
} from 'lucide-react';
import { useLanguage } from '../../context/AppContext';
import FoodItemEditor from '../FoodItemEditor';
import MacroVisualBars from '../MacroVisualBars';
import { shuffleFacts } from '../../constants/nutritionFacts';
import { markFactRead, KNOWLEDGE_READ_THRESHOLD_MS } from '../../utils/knowledgeTracker';
import { HeroMaleCelebrate } from '../HeroBadges';
import FatSecretBadge from '../FatSecretBadge';
import ScanTelemetry from '../../utils/scanTelemetry';

/**
 * Safe number coercion — accepts any value (number, string, null, undefined)
 * and returns a finite Number. Critical for any *.toFixed() call below
 * because the LLM occasionally returns numeric fields as strings.
 */
const safeNumber = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Meal-analysis loader — Avr. 2026 (post-stabilisation).
 *
 * Ultra-light core (zero CPU work during photo upload) PLUS optional
 * progressive UI that only kicks in AFTER the upload is done and we're
 * waiting on the LLM:
 *   • While `optimizingImage=true` (the few hundred ms of compression):
 *     pure CSS spinner, no setTimeout, no setInterval, no carousel, no facts.
 *   • Once compression is done, the "analyzing" phase rotates step labels
 *     and shows a "Did you know?" card to make the 10-15 s LLM wait pleasant.
 *   • All timers are cleaned up on unmount, never run during compression.
 */
const AnalyzingMealLoader = ({ language, optimizingImage, streamPhase, streamLLMElapsedMs, onAbortAnalysis }) => {
  const fr = (language || 'fr').startsWith('fr');
  // Extended step ladder — 2026-05-01: the loader used to freeze on step 4
  // ("Almost there...") forever on slow scans (14-25 s). Added 3 more stages
  // so users never see a stale label, plus a visible elapsed timer to prove
  // the app is still working on slow mobile networks.
  const steps = fr
    ? [
        'Analyse en cours...',
        'Identification des aliments...',
        'Calcul de la valeur nutritionnelle...',
        'Vérification des portions...',
        'Détection des micro-nutriments...',
        'Finalisation — presque terminé...',
        'Quelques secondes encore...',
      ]
    : [
        'Analyzing...',
        'Identifying foods...',
        'Computing nutrition...',
        'Checking portions...',
        'Detecting micro-nutrients...',
        'Finalizing — almost done...',
        'Just a few more seconds...',
      ];

  // Iter. 404 · Lot #13 — Real backend phases override the timed carousel.
  // When the streaming endpoint is active, `streamPhase` is the latest event
  // we received from the server. Map it to a localized label that reflects
  // *actual* backend state, not a guess based on a setTimeout.
  const PHASE_LABELS_FR = {
    received: '🛰️ Photo reçue par le serveur...',
    validating: '🛡️ Vérification de l\'image...',
    cache_check: '⚡ Vérification du cache nutritionnel...',
    cache_hit: '⚡ Hit du cache ! Repas déjà connu...',
    llm_start: '🤖 Analyse IA démarrée...',
    llm_progress: '🍴 Identification des aliments en cours...',
    llm_done: '✨ Aliments identifiés ! Enrichissement...',
    enriching: '📊 Calcul des macros (FatSecret + OMS)...',
    persisting: '💾 Enregistrement du repas...',
    complete: '✅ Terminé !',
  };
  const PHASE_LABELS_EN = {
    received: '🛰️ Photo received by server...',
    validating: '🛡️ Validating image...',
    cache_check: '⚡ Checking nutrition cache...',
    cache_hit: '⚡ Cache hit! Already known meal...',
    llm_start: '🤖 AI analysis started...',
    llm_progress: '🍴 Identifying foods...',
    llm_done: '✨ Foods identified! Enriching...',
    enriching: '📊 Computing macros (FatSecret + WHO)...',
    persisting: '💾 Saving meal...',
    complete: '✅ Done!',
  };

  const [stepIdx, setStepIdx] = React.useState(0);
  const [facts] = React.useState(() => shuffleFacts());
  const [factIdx, setFactIdx] = React.useState(0);
  const [showFacts, setShowFacts] = React.useState(false);
  // Elapsed timer — starts counting once compression is done so the user
  // always sees a forward-ticking indicator instead of a frozen label.
  const [elapsed, setElapsed] = React.useState(0);

  // Step rotation — extended ladder so we never freeze on a single label.
  React.useEffect(() => {
    if (optimizingImage) return;
    const t1 = setTimeout(() => setStepIdx(1), 1500);
    const t2 = setTimeout(() => setStepIdx(2), 4000);
    const t3 = setTimeout(() => setStepIdx(3), 7500);
    const t4 = setTimeout(() => setStepIdx(4), 11000);
    const t5 = setTimeout(() => setStepIdx(5), 15000);
    const t6 = setTimeout(() => setStepIdx(6), 20000);
    const tFacts = setTimeout(() => setShowFacts(true), 1000);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      clearTimeout(t4); clearTimeout(t5); clearTimeout(t6);
      clearTimeout(tFacts);
    };
  }, [optimizingImage]);

  // Elapsed counter — 1s tick, reset when compression re-runs.
  React.useEffect(() => {
    if (optimizingImage) { setElapsed(0); return; }
    const i = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(i);
  }, [optimizingImage]);

  // 🛡️ Indestructible telemetry — fire ONCE when the loader is open for 60+ s.
  // That's the threshold past which we consider the backend silently dead.
  const stuckFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (optimizingImage) { stuckFiredRef.current = false; return; }
    if (elapsed >= 60 && !stuckFiredRef.current) {
      stuckFiredRef.current = true;
      ScanTelemetry.trackEvent('scan_loader_stuck_over_60s', {
        elapsed_seconds: elapsed,
        endpoint: 'analyze',
      });
    }
  }, [elapsed, optimizingImage]);

  // Fact carousel — rotation 5.5s pour avoir le temps de lire le fait au complet.
  // Pausée pendant la phase compression pour ne pas tourner pendant l'encode canvas.
  React.useEffect(() => {
    if (optimizingImage || !showFacts) return;
    const interval = setInterval(() => setFactIdx((i) => (i + 1) % facts.length), 5500);
    return () => clearInterval(interval);
  }, [facts.length, optimizingImage, showFacts]);

  // 🎓 Knowledge tracker — count a fact as "read" after 3 s on screen.
  // Fires a celebratory toast when a milestone (10 / 25 / 50 / 71) is hit.
  React.useEffect(() => {
    if (optimizingImage || !showFacts) return;
    const currentFact = facts[factIdx];
    if (!currentFact) return;
    const t = setTimeout(() => {
      const milestone = markFactRead(currentFact);
      if (milestone) {
        // Use a delayed celebratory toast so it doesn't compete with the
        // analysis-result toast about to land.
        setTimeout(() => {
          toast.success(fr ? milestone.fr : milestone.en, {
            duration: 7000,
            description: fr
              ? '🌟 Ton coach est fier — continue à apprendre à chaque scan !'
              : '🌟 Your coach is proud — keep learning with every scan!',
          });
        }, 800);
      }
    }, KNOWLEDGE_READ_THRESHOLD_MS);
    return () => clearTimeout(t);
  }, [factIdx, optimizingImage, showFacts, facts, fr]);

  const currentFact = facts[factIdx];
  // Iter. 404 · Lot #13 — Prefer real backend phase over the timed carousel.
  // Smart fallback: if the streaming proxy is buffering (no phase advance
  // for >2.5 s while we're past `received`), fall through to the timed
  // carousel so the user still sees progressive feedback.
  const PHASE_LABELS = fr ? PHASE_LABELS_FR : PHASE_LABELS_EN;
  const [streamStuck, setStreamStuck] = React.useState(false);
  const lastPhaseRef = React.useRef({ phase: null, at: 0 });
  React.useEffect(() => {
    if (!streamPhase) return undefined;
    const now = Date.now();
    if (lastPhaseRef.current.phase !== streamPhase) {
      lastPhaseRef.current = { phase: streamPhase, at: now };
      setStreamStuck(false);
    }
    // If we're stuck on `received` / `validating` for >2.5 s, the proxy
    // is buffering. Surrender real-time and let the timed carousel drive.
    const t = setTimeout(() => {
      if (Date.now() - lastPhaseRef.current.at >= 2500
          && ['received', 'validating', 'cache_check'].includes(lastPhaseRef.current.phase)) {
        setStreamStuck(true);
      }
    }, 2600);
    return () => clearTimeout(t);
  }, [streamPhase]);
  const phaseLabel = (streamPhase && !streamStuck) ? PHASE_LABELS[streamPhase] : null;
  const displayLabel = optimizingImage
    ? (fr ? 'Optimisation de l\'image...' : 'Optimizing image...')
    : (phaseLabel || steps[stepIdx]);

  return (
    <div
      className="flex flex-col items-center justify-center py-12 gap-4"
      data-testid="meal-analyzing-loader"
    >
      {/* Static circular ring — pure CSS, no JS animation tick. */}
      <div
        className="w-12 h-12 rounded-full border-4 border-muted border-t-primary"
        style={{ animation: 'yumix-spin 1.2s linear infinite' }}
        aria-label="loading"
        role="status"
      />
      <p
        className="text-muted-foreground text-sm font-medium tracking-wide transition-opacity duration-300"
        key={optimizingImage ? 'optimizing' : stepIdx}
        data-testid="loader-status-text"
      >
        {displayLabel}
      </p>

      {/* Elapsed timer + reassurance — critical UX for slow mobile networks.
          After 15 s without feedback users start thinking the app crashed.
          This keeps a visible tick so they know the analysis is still live. */}
      {!optimizingImage && (
        <div className="flex flex-col items-center gap-1" data-testid="meal-analyzing-elapsed">
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-primary/80 font-mono">
            {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
          </span>
          {/* Iter. 404.e — Progressive escape-hatch messaging. After 25s the
              user is past P95 (~16s). After 45s we explicitly offer a way
              out (close + retry) to prevent the modal from feeling
              hostage-taking. */}
          {elapsed >= 12 && elapsed < 25 && (
            <span className="text-[10px] text-muted-foreground/80 italic">
              {fr
                ? 'Connexion lente détectée · on tient bon 💪'
                : 'Slow network detected · hang tight 💪'}
            </span>
          )}
          {elapsed >= 25 && elapsed < 45 && (
            <span className="text-[10px] text-amber-400 italic">
              {fr
                ? "Ça prend plus longtemps que prévu… ton IA travaille fort"
                : "Taking longer than usual… AI is working hard"}
            </span>
          )}
          {elapsed >= 45 && (
            <div className="mt-1 flex flex-col items-center gap-1.5">
              <span className="text-[10px] text-pink-400 italic">
                {fr
                  ? "Le serveur traîne. Tu peux fermer ce dialogue et réessayer."
                  : "Server is stalling. Feel free to close and retry."}
              </span>
              {onAbortAnalysis && (
                <button
                  type="button"
                  onClick={onAbortAnalysis}
                  className="text-[10px] px-3 py-1 rounded-full bg-muted hover:bg-muted/80 text-foreground font-semibold transition-colors"
                  data-testid="meal-analysis-abort-btn"
                >
                  ✕ {fr ? 'Annuler et réessayer' : 'Cancel and retry'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step progress dots — only during the analyzing phase, hidden during compression */}
      {!optimizingImage && (
        <div className="flex gap-1.5">
          {steps.slice(0, 3).map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${i <= stepIdx ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
      )}

      {/* Did-you-know card — keeps the user engaged during the LLM wait */}
      {showFacts && !optimizingImage && currentFact && (
        <div
          key={factIdx}
          className="mt-3 w-full max-w-sm bg-secondary/40 border border-border/60 rounded-xl p-3.5 text-center animate-in fade-in duration-500"
          data-testid="meal-did-you-know"
        >
          <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-1.5">
            💡 {fr ? 'Le saviez-vous ?' : 'Did you know?'}
          </div>
          <p className="text-xs text-foreground leading-relaxed">
            {fr ? currentFact.fr : currentFact.en}
          </p>
          <div className="text-[10px] text-muted-foreground italic mt-1.5">
            {fr ? 'Source' : 'Source'} : {currentFact.src}
          </div>
        </div>
      )}

      {/* Inline keyframes so we don't depend on Tailwind's animate-spin. */}
      <style>{`@keyframes yumix-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/**
 * 🛡️ ComplianceBadges — discreet OMS / Santé Canada chips based on the
 * post-AI nutrition_compliance validator (backend/services/nutrition_compliance.py).
 *
 * Renders ONLY when the backend has populated `compliance_status` on the
 * analysis object. Pure presentation, zero logic — the heavy lifting is
 * already done server-side.
 *
 * Display rules:
 *   • compliance_status === 'ok' AND no flags → small green "Conforme OMS" badge
 *   • oms_flags includes "high_sodium:Food:Xmg" → orange chip "Sodium élevé · Food"
 *   • oms_flags includes "high_sugar:Food:Xg" → orange chip "Sucre élevé · Food"
 *   • compliance_warnings includes "macro_drift:N%" → small grey chip "Macros à valider"
 *
 * No badge at all if `compliance_status` is missing (older cached responses).
 */
const ComplianceBadges = ({ analysis, language }) => {
  if (!analysis || !analysis.compliance_status) return null;
  const fr = (language || 'fr').startsWith('fr');
  const flags = Array.isArray(analysis.oms_flags) ? analysis.oms_flags : [];
  const warnings = Array.isArray(analysis.compliance_warnings) ? analysis.compliance_warnings : [];

  // Parse "high_sodium:Burger:2400mg" into { type, food, value }
  const parsed = flags.map((f) => {
    const m = String(f).match(/^(high_sodium|high_sugar):(.+?):(.+)$/);
    return m ? { type: m[1], food: m[2], value: m[3] } : null;
  }).filter(Boolean);

  const hasMacroDrift = warnings.some((w) => String(w).startsWith('macro_drift'));
  const allClear = analysis.compliance_status === 'ok' && parsed.length === 0 && !hasMacroDrift;

  if (allClear) {
    return (
      <div className="flex items-center gap-2 mt-1 mb-2" data-testid="compliance-ok-badge">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
          <Shield className="w-3 h-3" />
          {fr ? 'Conforme OMS / Santé Canada' : 'WHO / Health Canada compliant'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1 mb-2" data-testid="compliance-warnings">
      {parsed.map((p, i) => (
        <span
          key={`${p.type}-${i}`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20"
          data-testid={`oms-flag-${p.type}`}
        >
          <AlertTriangle className="w-3 h-3" />
          {p.type === 'high_sodium'
            ? (fr ? `Sodium élevé · ${p.food} (${p.value})` : `High sodium · ${p.food} (${p.value})`)
            : (fr ? `Sucre élevé · ${p.food} (${p.value})` : `High sugar · ${p.food} (${p.value})`)}
        </span>
      ))}
      {hasMacroDrift && (
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-muted text-muted-foreground border border-border"
          data-testid="macro-drift-chip"
        >
          {fr ? 'Macros à valider' : 'Macros to verify'}
        </span>
      )}
    </div>
  );
};

/**
 * Generate the "Verdict du Coach" — 4-Pillar Metabolic Analysis
 * Analyzes: Glycemic Radar, Satiety, Hypertrophy (MPS), Inflammation
 */
function generateVerdictDuCoach(analysis, language) {
  const fr = language === 'fr';
  const verdicts = [];

  // 🛡️ Defensive number coercion — the LLM occasionally returns numeric
  // fields as strings. Without this, the totals reduce or any toFixed call
  // crashes the modal (caught by MealAnalysisErrorBoundary as
  // "Analyse difficile"), even though the meal saved fine in DB.
  const protein = safeNumber(analysis?.total_protein_g);
  const carbs = safeNumber(analysis?.total_carbs_g);
  const fats = safeNumber(analysis?.total_fats_g);
  const fiber = safeNumber(analysis?.total_fiber_g);
  const calories = safeNumber(analysis?.total_calories);
  const foods = Array.isArray(analysis?.foods_detected) ? analysis.foods_detected : [];
  const processedFoods = Array.isArray(analysis?.processed_foods_detected) ? analysis.processed_foods_detected : [];
  const hasProcessed = foods.some(f => f.is_processed) || processedFoods.length > 0;

  // Detect ultra-processed protein sources (sausages, hot-dogs, charcuterie)
  const processedProteinKeywords = ['saucisse', 'hot-dog', 'hotdog', 'hot dog', 'charcuterie', 'bacon', 'pepperoni', 'salami', 'bologne', 'bologna', 'spam', 'nugget', 'corn dog', 'simili', 'sausage', 'wiener', 'frankfurter', 'deli meat'];
  // Coerce each item to a lowercase string. OpenAI Vision occasionally returns
  // objects (`{name: "saucisse"}`) instead of plain strings — `.toLowerCase()`
  // would crash without this guard. Defensive on top of backend normalization.
  const toFoodName = (x) => {
    if (typeof x === 'string') return x.toLowerCase();
    if (x && typeof x === 'object') {
      const v = x.name || x.item || x.label || x.food || '';
      return typeof v === 'string' ? v.toLowerCase() : '';
    }
    return '';
  };
  const allFoodNames = [
    ...foods.map(f => toFoodName(f?.name)),
    ...processedFoods.map(toFoodName),
  ];
  const hasProcessedProtein = allFoodNames.some(name =>
    processedProteinKeywords.some(kw => name.includes(kw))
  );

  // PILLAR 4 FIRST (needed for Pillar 1 cross-check): Inflammation
  let inflammationRed = false;
  let inflammationVerdict;
  if (hasProcessed) {
    inflammationRed = true;
    // Coerce each entry to a printable string (handles object payloads from AI).
    const toLabel = (x) => {
      if (typeof x === 'string') return x;
      if (x && typeof x === 'object') return x.name || x.item || x.label || x.food || '';
      return '';
    };
    const badItems = processedFoods.length > 0
      ? processedFoods.map(toLabel).filter(Boolean).join(', ')
      : foods.filter(f => f.is_processed).map(f => toLabel(f?.name)).filter(Boolean).join(', ');
    inflammationVerdict = {
      emoji: '🔴',
      label: fr ? 'Inflammation' : 'Inflammation',
      text: fr ? `Ingredients inflammatoires detectes (${badItems}). Privilegie les aliments non-transformes.` : `Inflammatory ingredients detected (${badItems}). Prefer unprocessed foods.`,
    };
  } else {
    inflammationVerdict = {
      emoji: '🟢',
      label: fr ? 'Inflammation' : 'Inflammation',
      text: fr ? 'Aucun aliment ultra-transformé détecté. Repas propre.' : 'No ultra-processed foods detected. Clean meal.',
    };
  }

  // PILLAR 1: Hypertrophy (MPS) — DOWNGRADED if processed protein source
  if (protein >= 25 && !hasProcessedProtein) {
    verdicts.push({
      emoji: '🟢',
      label: fr ? 'Muscle' : 'Muscle',
      text: fr ? 'Protéines de haute qualité validées. MPS activée.' : 'High quality proteins validated. MPS activated.',
    });
  } else if (protein >= 25 && hasProcessedProtein) {
    // FIX #2: Processed protein = never green
    verdicts.push({
      emoji: '🟠',
      label: fr ? 'Muscle' : 'Muscle',
      text: fr ? `Quota atteint (${protein.toFixed(0)}g), mais source de proteines sous-optimale/transformee. Privilegie poulet, oeuf, poisson, whey.` : `Quota met (${protein.toFixed(0)}g), but sub-optimal/processed protein source. Prefer chicken, egg, fish, whey.`,
    });
  } else if (protein >= 15) {
    verdicts.push({
      emoji: '🟠',
      label: fr ? 'Muscle' : 'Muscle',
      text: fr ? `${protein.toFixed(0)}g de proteines — correct mais vise 25g+ par repas pour maximiser la synthese musculaire.` : `${protein.toFixed(0)}g protein — decent but aim for 25g+ per meal to maximize MPS.`,
    });
  } else {
    verdicts.push({
      emoji: '🔴',
      label: fr ? 'Muscle' : 'Muscle',
      text: fr ? `Seulement ${protein.toFixed(0)}g de proteines. Insuffisant pour la MPS. Ajoute une source (oeuf, poulet, whey).` : `Only ${protein.toFixed(0)}g protein. Insufficient for MPS. Add a source (egg, chicken, whey).`,
    });
  }

  // PILLAR 2: Glycemic Radar — STRICT RULE
  // FIX #1: If carbs > 50g AND fiber < 10g → always RED
  let glycemicRed = false;
  if (carbs > 50 && fiber < 10) {
    glycemicRed = true;
    verdicts.push({
      emoji: '🔴',
      label: fr ? 'Glycémie' : 'Glycemic',
      text: fr ? `Alerte pic d'insuline : Charge glycémique très élevée (${carbs.toFixed(0)}g glucides, seulement ${fiber.toFixed(0)}g fibres). Risque de crash d'energie.` : `Insulin spike alert: Very high glycemic load (${carbs.toFixed(0)}g carbs, only ${fiber.toFixed(0)}g fiber). Energy crash risk.`,
    });
  } else if (carbs < 10) {
    verdicts.push({
      emoji: '🟢',
      label: fr ? 'Glycémie' : 'Glycemic',
      text: fr ? 'Très faible charge glycémique. Insuline stable.' : 'Very low glycemic load. Insulin stable.',
    });
  } else {
    const carbsFiberRatio = fiber > 0 ? carbs / fiber : carbs;
    if (carbsFiberRatio <= 6 && fiber >= 5) {
      verdicts.push({
        emoji: '🟢',
        label: fr ? 'Glycémie' : 'Glycemic',
        text: fr ? 'Bon ratio glucides/fibres. Pic d\'insuline contrôle.' : 'Good carb/fiber ratio. Insulin spike controlled.',
      });
    } else if (carbsFiberRatio <= 12) {
      verdicts.push({
        emoji: '🟠',
        label: fr ? 'Glycémie' : 'Glycemic',
        text: fr ? 'Ratio glucides/fibres moyen. Ajoute des legumes ou des fibres pour ralentir l\'absorption.' : 'Average carb/fiber ratio. Add veggies or fiber to slow absorption.',
      });
    } else {
      glycemicRed = true;
      verdicts.push({
        emoji: '🔴',
        label: fr ? 'Glycémie' : 'Glycemic',
        text: fr ? `Alerte pic d'insuline : ${carbs.toFixed(0)}g de glucides avec seulement ${fiber.toFixed(0)}g de fibres. Risque de crash energetique.` : `Insulin spike alert: ${carbs.toFixed(0)}g carbs with only ${fiber.toFixed(0)}g fiber. Energy crash risk.`,
      });
    }
  }

  // PILLAR 3: Satiety
  const satieteScore = (protein * 1.5) + (fiber * 2) + (fats * 0.8);
  if (satieteScore >= 60 && !hasProcessed) {
    verdicts.push({
      emoji: '🟢',
      label: fr ? 'Satiété' : 'Satiety',
      text: fr ? 'Ce repas va bien te soutenir. Bonne combinaison protéines + fibres + lipides.' : 'This meal will sustain you well. Good protein + fiber + fat combo.',
    });
  } else if (satieteScore >= 35) {
    verdicts.push({
      emoji: '🟠',
      label: fr ? 'Satiété' : 'Satiety',
      text: fr ? 'Satiété moyenne. Ajoute des fibres ou des protéines si tu as faim rapidement.' : 'Average satiety. Add fiber or protein if you get hungry fast.',
    });
  } else {
    verdicts.push({
      emoji: '🔴',
      label: fr ? 'Satiété' : 'Satiety',
      text: fr ? 'Faible pouvoir rassasiant. Tu risques d\'avoir faim dans 1-2h. Prévois une collation protéines.' : 'Low satiety power. You may be hungry in 1-2h. Plan a protein snack.',
    });
  }

  // PILLAR 4: Inflammation (add last)
  verdicts.push(inflammationVerdict);

  // FIX #3: Attach red flags for the UI banner veto system
  verdicts._hasRedFlag = inflammationRed || glycemicRed || calories > 1000;
  verdicts._calorieOverload = calories > 1000;

  return verdicts;
}

// ─────────────────────────────────────────────────────────────────────
// Iter. 411.mm3 — Two-pass audit badge
// Shown only when the second AI verifier ran. Compact by default; expands
// on tap to reveal exactly what was added/adjusted/removed + the macro
// delta so the user understands why numbers moved vs a single-pass scan.
// ─────────────────────────────────────────────────────────────────────
const TwoPassBadge = ({ meta, language }) => {
  const [expanded, setExpanded] = React.useState(false);
  if (!meta) return null;

  const L = (fr, en, es) => language === 'es' ? es : (language === 'en' ? en : fr);
  const delta = meta.delta || {};
  const hasChanges =
    (meta.foods_added_names?.length || 0) > 0 ||
    (meta.foods_removed_names?.length || 0) > 0 ||
    (meta.portions_adjusted_names?.length || 0) > 0;

  const fmt = (n) => (n > 0 ? `+${n.toFixed(0)}` : n.toFixed(0));
  const dCal = delta.calories || 0;
  const dProt = delta.protein_g || 0;

  return (
    <button
      type="button"
      onClick={() => hasChanges && setExpanded((e) => !e)}
      data-testid="two-pass-badge"
      className="w-full text-left rounded-lg overflow-hidden transition-all active:scale-[0.99]"
      style={{
        background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(15,15,15,0.7) 45%, rgba(147,51,234,0.20) 100%)',
        border: '1px solid rgba(147,51,234,0.45)',
        boxShadow: '0 0 14px rgba(147,51,234,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
        backdropFilter: 'blur(6px)',
        cursor: hasChanges ? 'pointer' : 'default',
      }}
    >
      <div className="flex items-center gap-3 p-3">
        <Sparkles
          className="w-5 h-5 text-purple-300 flex-shrink-0"
          style={{ filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.7))' }}
        />
        <div className="flex-1 min-w-0">
          <p className="font-heading text-xs uppercase tracking-wider text-purple-200"
            style={{ textShadow: '0 0 6px rgba(168,85,247,0.5)' }}>
            {hasChanges
              ? L('Double passe IA — corrections appliquées', 'Two-pass AI — corrections applied', 'Doble pasada IA — correcciones aplicadas')
              : L('Double passe IA — analyse confirmée', 'Two-pass AI — analysis confirmed', 'Doble pasada IA — analisis confirmado')}
          </p>
          {meta.audit_notes && (
            <p className="text-xs text-purple-100/70 mt-1 line-clamp-2">
              {meta.audit_notes}
            </p>
          )}
        </div>
        {hasChanges && (
          <div className="text-right flex-shrink-0">
            <p className="font-black text-sm text-amber-300"
              style={{ textShadow: '0 0 6px rgba(252,211,77,0.6)' }}>
              {fmt(dCal)} kcal
            </p>
            <p className="text-xs text-orange-300/80">{fmt(dProt)}g prot</p>
          </div>
        )}
      </div>

      {expanded && hasChanges && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-purple-500/20">
          {meta.foods_added_names?.length > 0 && (
            <div className="text-xs">
              <span className="text-emerald-300 font-semibold">
                + {L('Ajoutés', 'Added', 'Añadidos')}:
              </span>{' '}
              <span className="text-white/80">{meta.foods_added_names.join(', ')}</span>
            </div>
          )}
          {meta.portions_adjusted_names?.length > 0 && (
            <div className="text-xs">
              <span className="text-amber-300 font-semibold">
                ↕ {L('Portions ajustées', 'Portions adjusted', 'Porciones ajustadas')}:
              </span>{' '}
              <span className="text-white/80">{meta.portions_adjusted_names.join(', ')}</span>
            </div>
          )}
          {meta.foods_removed_names?.length > 0 && (
            <div className="text-xs">
              <span className="text-rose-300 font-semibold">
                − {L('Retirés', 'Removed', 'Eliminados')}:
              </span>{' '}
              <span className="text-white/80">{meta.foods_removed_names.join(', ')}</span>
            </div>
          )}
          <div className="text-[10px] text-purple-200/50 mt-2 uppercase tracking-wide">
            {L('Confiance', 'Confidence', 'Confianza')}: {meta.confidence || 'medium'}
          </div>
        </div>
      )}
    </button>
  );
};



const MealAnalysisModal = ({
  open,
  onOpenChange,
  analyzing,
  optimizingImage,
  // Iter. 404 · Lot #13 — real-time phase from /meals/analyze-stream
  streamPhase,
  streamLLMElapsedMs,
  // Iter. 404.e — escape hatch when the modal has been stuck >45s
  onAbortAnalysis,
  analysisResult,
  // Nutrition editing
  isEditingNutrition,
  editedNutrition,
  onEditedNutritionChange,
  savingNutrition,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  // Food item editing
  editableFoods,
  onFoodsChange,
  onSaveFoodsEdit,
  // Actions
  onSaveAsFavorite,
  onClose,
  // Translations
  t
}) => {
  const { language } = useLanguage();

  // 🦸 Hero celebration: fires once per analysis when compliance is OK.
  // Tracks the current analysis_id to avoid re-firing on re-renders or
  // food edits during the same meal session.
  const [heroCelebrate, setHeroCelebrate] = React.useState(false);
  const lastCelebratedRef = React.useRef(null);
  React.useEffect(() => {
    if (!analysisResult || analyzing) return;
    const status = analysisResult?.analysis?.compliance_status;
    const id = analysisResult?.analysis?.analysis_id || analysisResult?.message;
    if (status === 'ok' && id && lastCelebratedRef.current !== id) {
      lastCelebratedRef.current = id;
      setHeroCelebrate(true);
    }
  }, [analysisResult, analyzing]);

  // 🛡️ Indestructible telemetry — detect silent UX failures:
  //   1. User closes the modal while still analyzing (`onOpenChange(false)` mid-flight)
  //   2. Parent unmounts while analyzing (route change, app backgrounded, OOM crash)
  const analyzingRef = React.useRef(analyzing);
  React.useEffect(() => { analyzingRef.current = analyzing; }, [analyzing]);
  React.useEffect(() => {
    return () => {
      // Component unmounted while analysis was still in flight → silent crash.
      if (analyzingRef.current) {
        ScanTelemetry.trackEvent('scan_unmount_during_analysis', {
          endpoint: 'analyze',
        });
      }
    };
  }, []);

  const handleOpenChange = React.useCallback((next) => {
    // User dismissed the modal mid-analysis → track + delegate to caller.
    if (!next && analyzingRef.current) {
      ScanTelemetry.trackEvent('scan_modal_dismissed_before_result', {
        endpoint: 'analyze',
      });
    }
    if (typeof onOpenChange === 'function') onOpenChange(next);
  }, [onOpenChange]);

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden"
        onPointerDownOutside={(e) => { if (analyzing) e.preventDefault(); }}
        onInteractOutside={(e) => { if (analyzing) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (analyzing) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight">
            {analyzing ? t('meal.analyzing') : t('meal.result')}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {analyzing 
              ? t('meal.analyzing_subtitle', 'Notre équipe analyse ton repas...')
              : t('meal.result_subtitle', "Voici l'analyse de ton repas")
            }
          </DialogDescription>
          {/* 🛡️ Source-of-truth badge — Yuri spec 2026-02 Iter. 316.
              Always visible after analysis: "Vérifié FatSecret 🇨🇦" or
              "Estimation IA". Lets the user know exactly which engine
              produced their macros. */}
          {!analyzing && analysisResult?.analysis && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <FatSecretBadge analysis={analysisResult.analysis} variant="full" language={language} />
            </div>
          )}
        </DialogHeader>
        {analyzing ? (
          <AnalyzingMealLoader
            language={language}
            optimizingImage={optimizingImage}
            streamPhase={streamPhase}
            streamLLMElapsedMs={streamLLMElapsedMs}
            onAbortAnalysis={onAbortAnalysis}
          />
        ) : !analysisResult || !analysisResult.analysis ? (
          /* Safety fallback — prevents the infamous "black screen" when the
             backend returns no analysis (error, veto, or empty payload). */
          <div className="flex flex-col items-center justify-center py-12 gap-4" data-testid="meal-analysis-fallback">
            <AlertTriangle className="w-12 h-12 text-amber-500" />
            <p className="text-muted-foreground text-center max-w-xs">
              {t('meal.fallback_error', "L'analyse n'a pas pu être chargée. Ferme cette fenêtre et réessaye.")}
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-primary text-primary-foreground"
              data-testid="meal-analysis-fallback-close"
            >
              <X className="w-4 h-4 mr-2" />
              {t('common.close', 'Fermer')}
            </Button>
          </div>
        ) : (
          (() => {
            // Compute verdict ONCE so we can use red flags for the banner
            const verdict = analysisResult.analysis ? generateVerdictDuCoach(analysisResult.analysis, language) : [];
            const hasRedFlag = verdict._hasRedFlag || false;
            const calorieOverload = verdict._calorieOverload || false;
            // FIX #3: Override protein_status if red flags detected
            const effectiveStatus = hasRedFlag ? 'flagged' : analysisResult.analysis.protein_status;
            
            return (
          <div className="space-y-4 overflow-hidden" style={{wordBreak: 'break-word', overflowWrap: 'break-word'}}>
            {/* Status Badge — VETOED if red flags — GLOW aesthetic (Iter. 411.ee) */}
            {(() => {
              const _rgb = effectiveStatus === 'flagged'
                ? '245,158,11'   // amber-500
                : effectiveStatus === 'on_track'
                  ? '251,146,60' // primary orange
                  : effectiveStatus === 'low'
                    ? '234,179,8'   // status-warning
                    : '239,68,68';  // destructive
              return (
                <div
                  className="p-4 rounded-lg"
                  style={{
                    background: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${_rgb},0.22) 100%)`,
                    border: `1px solid rgba(${_rgb},0.55)`,
                    boxShadow: `0 0 14px rgba(${_rgb},0.45), inset 0 1px 0 rgba(255,255,255,0.06)`,
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src="/mascot-yuri.png"
                      alt="Coach Yuri"
                      className="w-10 h-10 object-contain flex-shrink-0"
                      style={{ filter: `drop-shadow(0 0 6px rgba(${_rgb},0.7))` }}
                    />
                    {effectiveStatus === 'flagged' ? (
                      <AlertTriangle className="w-6 h-6" style={{ color: '#fbbf24', filter: `drop-shadow(0 0 5px rgba(${_rgb},0.8))` }} />
                    ) : effectiveStatus === 'on_track' ? (
                      <CheckCircle2 className="w-6 h-6" style={{ color: '#fb923c', filter: `drop-shadow(0 0 5px rgba(${_rgb},0.8))` }} />
                    ) : (
                      <AlertTriangle className="w-6 h-6" style={{ color: effectiveStatus === 'low' ? '#eab308' : '#ef4444', filter: `drop-shadow(0 0 5px rgba(${_rgb},0.8))` }} />
                    )}
                    <span
                      className="font-bold"
                      style={{ color: `rgba(${_rgb},1)`, textShadow: `0 0 6px rgba(${_rgb},0.55)` }}
                    >
                      {effectiveStatus === 'flagged'
                        ? t('meal.status_flagged', "Repas enregistré. Regarde l'analyse du coach.")
                        : effectiveStatus === 'on_track'
                        ? t('common.onTrack')
                        : effectiveStatus === 'low'
                        ? t('common.low')
                        : t('common.high')
                      }
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Macros Summary */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-heading text-sm uppercase tracking-tight text-muted-foreground">
                  {t('meal.macros', 'Macros')}
                </h4>
                {!isEditingNutrition ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onStartEdit}
                    className="text-primary hover:text-primary/80 h-8 px-2"
                    data-testid="edit-nutrition-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    {t('common.edit', 'Modifier')}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onCancelEdit}
                      className="text-muted-foreground hover:text-foreground h-8 px-2"
                    >
                      <X className="w-4 h-4 mr-1" />
                      {t('common.cancel', 'Annuler')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={onSaveEdit}
                      disabled={savingNutrition}
                      className="bg-primary text-primary-foreground h-8 px-3"
                      data-testid="save-nutrition-btn"
                    >
                      {savingNutrition ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-1" />
                      )}
                      {t('common.save', 'Sauvegarder')}
                    </Button>
                  </div>
                )}
              </div>
              {isEditingNutrition ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('dashboard.protein')} (g)</label>
                    <Input
                      type="number"
                      value={editedNutrition.total_protein_g}
                      onChange={(e) => onEditedNutritionChange({...editedNutrition, total_protein_g: parseFloat(e.target.value) || 0})}
                      className="bg-secondary border-primary/30 h-10"
                      data-testid="edit-protein-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('dashboard.calories')}</label>
                    <Input
                      type="number"
                      value={editedNutrition.total_calories}
                      onChange={(e) => onEditedNutritionChange({...editedNutrition, total_calories: parseFloat(e.target.value) || 0})}
                      className="bg-secondary border-border h-10"
                      data-testid="edit-calories-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('meal.field_carbs_g', 'Glucides (g)')}</label>
                    <Input
                      type="number"
                      value={editedNutrition.total_carbs_g}
                      onChange={(e) => onEditedNutritionChange({...editedNutrition, total_carbs_g: parseFloat(e.target.value) || 0})}
                      className="bg-secondary border-border h-10"
                      data-testid="edit-carbs-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('meal.field_fats_g', 'Lipides (g)')}</label>
                    <Input
                      type="number"
                      value={editedNutrition.total_fats_g}
                      onChange={(e) => onEditedNutritionChange({...editedNutrition, total_fats_g: parseFloat(e.target.value) || 0})}
                      className="bg-secondary border-border h-10"
                      data-testid="edit-fats-input"
                    />
                  </div>
                </div>
              ) : (
                <>
                  {/* 🍁 Macros visuels FatSecret avec badge "Source réelle vs Estimation IA" */}
                  <MacroVisualBars analysis={analysisResult.analysis} />

                  {/* 🛡️ OMS / Santé Canada compliance — discreet badges based on post-AI validator */}
                  <ComplianceBadges analysis={analysisResult.analysis} language={language} />

                <div className="grid grid-cols-2 gap-4">
                  {/* Iter. 411.ee — 4 macro cards in unified GLOW aesthetic */}
                  {[
                    { label: t('dashboard.protein'), value: safeNumber(analysisResult.analysis.total_protein_g).toFixed(1) + 'g', rgb: '251,146,60', big: true },
                    { label: t('dashboard.calories'), value: safeNumber(analysisResult.analysis.total_calories).toFixed(0), rgb: '245,158,11', big: true },
                    { label: t('meal.carbs', 'Glucides'), value: safeNumber(analysisResult.analysis.total_carbs_g).toFixed(1) + 'g', rgb: '59,130,246', big: false },
                    { label: t('meal.fats', 'Lipides'), value: safeNumber(analysisResult.analysis.total_fats_g).toFixed(1) + 'g', rgb: '234,179,8', big: false },
                  ].map((m, i) => (
                    <div
                      key={i}
                      className={m.big ? 'p-4 rounded-lg' : 'p-3 rounded-lg'}
                      style={{
                        background: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${m.rgb},0.20) 100%)`,
                        border: `1px solid rgba(${m.rgb},0.45)`,
                        boxShadow: `0 0 12px rgba(${m.rgb},0.35), inset 0 1px 0 rgba(255,255,255,0.05)`,
                        backdropFilter: 'blur(6px)',
                      }}
                    >
                      <p
                        className={`${m.big ? 'text-sm' : 'text-xs'} uppercase tracking-widest font-black`}
                        style={{ color: `rgba(${m.rgb},0.85)`, textShadow: `0 0 4px rgba(${m.rgb},0.4)` }}
                      >
                        {m.label}
                      </p>
                      <p
                        className={`font-heading ${m.big ? 'text-2xl' : 'text-lg'} font-black`}
                        style={{ color: `rgba(${m.rgb},1)`, textShadow: `0 0 8px rgba(${m.rgb},0.6)` }}
                      >
                        {m.value}
                      </p>
                    </div>
                  ))}
                  <div
                    className="p-3 rounded-lg col-span-2"
                    style={{
                      background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(34,197,94,0.20) 100%)',
                      border: '1px solid rgba(34,197,94,0.5)',
                      boxShadow: '0 0 12px rgba(34,197,94,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <p
                        className="text-xs uppercase tracking-widest font-black"
                        style={{ color: 'rgba(34,197,94,0.85)', textShadow: '0 0 4px rgba(34,197,94,0.4)' }}
                      >
                        {t('meal.fiber', 'Fibres')}
                      </p>
                      <p
                        className="font-heading text-lg font-black"
                        style={{ color: 'rgba(34,197,94,1)', textShadow: '0 0 8px rgba(34,197,94,0.6)' }}
                      >
                        {safeNumber(analysisResult.analysis.total_fiber_g).toFixed(1)}g
                      </p>
                    </div>
                  </div>
                </div>
                </>
              )}
              {!isEditingNutrition && (
                <p className="text-xs text-muted-foreground text-center">
                  {t('meal.adjust_hint', '⬇️ Ajuste les quantités ci-dessous pour recalculer automatiquement')}
                </p>
              )}
            </div>

            {/* LE VERDICT DU COACH — 4 Pillar Metabolic Analysis (GLOW aesthetic — Iter. 411.ff) */}
            {!isEditingNutrition && analysisResult.analysis && verdict.length > 0 && (
              <div
                className="rounded-xl p-4 space-y-3"
                data-testid="verdict-du-coach"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(15,15,15,0.7) 45%, rgba(251,146,60,0.20) 100%)',
                  border: '1px solid rgba(251,146,60,0.5)',
                  boxShadow: '0 0 16px rgba(251,146,60,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <div className="flex items-center gap-2.5">
                  <Shield
                    className="w-5 h-5"
                    style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.9))' }}
                  />
                  <h4
                    className="font-heading text-sm uppercase tracking-widest font-black"
                    style={{ color: '#fed7aa', textShadow: '0 0 6px rgba(251,146,60,0.6)' }}
                  >
                    {t('meal.coach_verdict', 'Le Verdict du Coach')}
                  </h4>
                </div>
                <div className="space-y-2">
                  {verdict.filter(v => v.emoji).map((v, i) => {
                    // Map emoji to rgb so each pillar row inherits its status color.
                    const rgb = v.emoji === '🟢' ? '34,197,94'     // green-500
                              : v.emoji === '🟠' ? '245,158,11'    // amber-500
                              : v.emoji === '🔴' ? '239,68,68'     // red-500
                              : '148,163,184';                     // slate-400 (fallback)
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-2.5 rounded-lg p-2.5"
                        data-testid={`verdict-pillar-${i}`}
                        style={{
                          background: `linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(15,15,15,0.5) 45%, rgba(${rgb},0.14) 100%)`,
                          border: `1px solid rgba(${rgb},0.35)`,
                          boxShadow: `0 0 8px rgba(${rgb},0.25), inset 0 1px 0 rgba(255,255,255,0.04)`,
                        }}
                      >
                        <span
                          className="text-base shrink-0 mt-0.5"
                          style={{ filter: `drop-shadow(0 0 5px rgba(${rgb},0.85))` }}
                          aria-hidden="true"
                        >
                          {v.emoji}
                        </span>
                        <p
                          className="text-sm leading-snug"
                          style={{ color: '#f8fafc' }}
                        >
                          <span
                            className="font-black uppercase tracking-wider"
                            style={{ color: `rgba(${rgb},1)`, textShadow: `0 0 5px rgba(${rgb},0.55)` }}
                          >
                            {v.label}
                          </span>
                          <span className="font-normal"> : </span>
                          <span className="text-white/85">{v.text}</span>
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Foods Detected — GLOW aesthetic (Iter. 411.ii) */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4
                  className="font-heading text-lg uppercase tracking-widest flex items-center gap-2 font-black"
                  style={{ color: '#fed7aa', textShadow: '0 0 6px rgba(251,146,60,0.55)' }}
                >
                  <UtensilsCrossed
                    className="w-5 h-5"
                    style={{ filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.85))' }}
                  />
                  {t('meal.foods')}
                </h4>
                <p className="text-xs text-white/60">
                  {t('meal.replace_hint', '✏️ Clique sur le crayon pour remplacer un aliment')}
                </p>
              </div>
              {editableFoods.length > 0 ? (
                <FoodItemEditor 
                  foods={editableFoods}
                  onFoodsChange={onFoodsChange}
                  readOnly={false}
                  showDetails={true}
                />
              ) : (
                <div className="space-y-2">
                  {analysisResult.analysis.foods_detected?.map((food, index) => {
                    const rgb = food.is_processed ? '239,68,68' : food.is_super_aliment ? '251,146,60' : '148,163,184';
                    return (
                      <div
                        key={index}
                        className="flex justify-between items-center p-3 rounded-lg"
                        style={{
                          background: `linear-gradient(135deg, rgba(0,0,0,0.68) 0%, rgba(15,15,15,0.62) 45%, rgba(${rgb},0.18) 100%)`,
                          border: `1px solid rgba(${rgb},0.4)`,
                          boxShadow: `0 0 10px rgba(${rgb},0.28), inset 0 1px 0 rgba(255,255,255,0.04)`,
                          backdropFilter: 'blur(4px)',
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {food.is_processed && <span style={{ filter: `drop-shadow(0 0 5px rgba(${rgb},0.85))` }}>⚠️</span>}
                          {food.is_super_aliment && <span style={{ filter: `drop-shadow(0 0 5px rgba(${rgb},0.85))` }}>⭐</span>}
                          <span style={{ color: `rgba(${rgb},1)`, textShadow: `0 0 5px rgba(${rgb},0.55)` }} className="font-black">
                            {food.name}
                          </span>
                        </div>
                        <span className="text-white/70 text-sm">
                          {safeNumber(food.protein_g).toFixed(1)}g prot • {safeNumber(food.calories).toFixed(0)} cal
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {editableFoods.length > 0 && (
                <button
                  type="button"
                  onClick={onSaveFoodsEdit}
                  disabled={savingNutrition}
                  data-testid="save-quantities-btn"
                  className="w-full mt-3 h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-60"
                  style={{
                    background: 'linear-gradient(140deg, #fb923c 0%, #f97316 55%, #c2410c 100%)',
                    color: '#fff',
                    boxShadow: '0 0 18px rgba(251,146,60,0.6), inset 0 1px 0 rgba(255,255,255,0.2)',
                    textShadow: '0 0 5px rgba(0,0,0,0.35)',
                  }}
                >
                  {savingNutrition ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.7))' }} />
                  )}
                  {t('meal.save_changes', 'Sauvegarder les modifications')}
                </button>
              )}
            </div>

            {/* Chef's Touch Suggestion — GLOW aesthetic (Iter. 411.ii) */}
            {analysisResult.analysis.chef_touch_suggestion && (
              <div
                className="p-3 sm:p-4 rounded-lg overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.20) 100%)',
                  border: '1px solid rgba(251,146,60,0.5)',
                  boxShadow: '0 0 14px rgba(251,146,60,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <div className="flex items-start gap-2 sm:gap-3 min-w-0">
                  <ChefHat
                    className="w-6 h-6 flex-shrink-0 mt-1"
                    style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.9))' }}
                  />
                  <div className="min-w-0 flex-1">
                    <h4
                      className="font-heading text-base sm:text-lg uppercase tracking-widest font-black mb-1"
                      style={{ color: '#fed7aa', textShadow: '0 0 6px rgba(251,146,60,0.6)' }}
                    >
                      {t('meal.suggestion')}
                    </h4>
                    <p className="text-white/85 text-sm sm:text-base break-words">
                      {analysisResult.analysis.chef_touch_suggestion}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Off-track Alert — GLOW aesthetic (Iter. 411.ii) */}
            {analysisResult.analysis.off_track_alert && (
              <div
                className="p-3 sm:p-5 rounded-lg overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(15,15,15,0.7) 45%, rgba(239,68,68,0.28) 100%)',
                  border: '1.5px solid rgba(239,68,68,0.65)',
                  boxShadow: '0 0 18px rgba(239,68,68,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <div className="flex items-start gap-2 sm:gap-4 min-w-0">
                  <div
                    className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(140deg, rgba(239,68,68,0.5), rgba(239,68,68,0.2))',
                      boxShadow: '0 0 12px rgba(239,68,68,0.6), inset 0 0 0 1px rgba(239,68,68,0.6)',
                    }}
                  >
                    <span className="text-xl sm:text-2xl" style={{ filter: 'drop-shadow(0 0 6px rgba(239,68,68,0.9))' }}>🚨</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading text-base sm:text-xl uppercase tracking-widest font-black mb-2 flex items-center gap-2"
                      style={{ color: '#fecaca', textShadow: '0 0 6px rgba(239,68,68,0.65)' }}
                    >
                      <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ filter: 'drop-shadow(0 0 5px rgba(239,68,68,0.85))' }} />
                      <span>{t('meal.offtrack_alert', 'Alerte Hors-Piste!')}</span>
                    </h4>
                    <p className="text-white/85 text-sm sm:text-base leading-relaxed break-words">
                      {analysisResult.analysis.off_track_alert}
                    </p>
                    {analysisResult.analysis.processed_foods_detected?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {analysisResult.analysis.processed_foods_detected.map((food, i) => {
                          const label = typeof food === 'string'
                            ? food
                            : (food?.name || food?.item || food?.label || food?.food || '');
                          if (!label) return null;
                          return (
                            <span
                              key={i}
                              className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full break-words font-black uppercase tracking-wider"
                              style={{
                                background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(239,68,68,0.28) 100%)',
                                border: '1px solid rgba(239,68,68,0.6)',
                                boxShadow: '0 0 8px rgba(239,68,68,0.4)',
                                color: '#fecaca',
                                textShadow: '0 0 4px rgba(239,68,68,0.55)',
                              }}
                            >
                              ❌ {label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Allergy Alert — GLOW aesthetic (Iter. 411.ii) */}
            {analysisResult.analysis.allergy_alert && (
              <div
                className="p-3 sm:p-5 rounded-lg animate-pulse overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.78) 0%, rgba(15,15,15,0.72) 45%, rgba(220,38,38,0.35) 100%)',
                  border: '2px solid rgba(220,38,38,0.75)',
                  boxShadow: '0 0 22px rgba(220,38,38,0.6), 0 0 6px rgba(220,38,38,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <div className="flex items-start gap-2 sm:gap-4 min-w-0">
                  <div
                    className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(140deg, rgba(220,38,38,0.55), rgba(220,38,38,0.25))',
                      boxShadow: '0 0 14px rgba(220,38,38,0.7), inset 0 0 0 1px rgba(220,38,38,0.65)',
                    }}
                  >
                    <span className="text-xl sm:text-2xl" style={{ filter: 'drop-shadow(0 0 6px rgba(220,38,38,0.9))' }}>⚠️</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading text-base sm:text-xl uppercase tracking-widest font-black mb-2 flex items-center gap-2"
                      style={{ color: '#fecaca', textShadow: '0 0 7px rgba(220,38,38,0.75)' }}
                    >
                      <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ filter: 'drop-shadow(0 0 5px rgba(220,38,38,0.9))' }} />
                      <span>{t('meal.allergen_alert', 'ALERTE ALLERGENE!')}</span>
                    </h4>
                    <p className="text-white/90 text-sm sm:text-base leading-relaxed font-medium break-words">
                      {analysisResult.analysis.allergy_alert}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* AI Feedback — GLOW aesthetic (Iter. 411.ii) "Salut Youri!" card */}
            <div
              className="p-3 sm:p-4 rounded-lg overflow-hidden"
              data-testid="ai-feedback-card"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.18) 100%)',
                border: '1px solid rgba(251,146,60,0.4)',
                boxShadow: '0 0 14px rgba(251,146,60,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <p className="italic text-sm sm:text-base break-words leading-relaxed" style={{ color: '#fed7aa' }}>
                <span style={{ color: '#fb923c', textShadow: '0 0 5px rgba(251,146,60,0.55)' }}>&ldquo;</span>
                <span className="text-white/90">{analysisResult.analysis.ai_feedback}</span>
                <span style={{ color: '#fb923c', textShadow: '0 0 5px rgba(251,146,60,0.55)' }}>&rdquo;</span>
              </p>
            </div>

            {/* Iter. 411.mm3 — Two-pass audit badge */}
            {analysisResult.analysis._two_pass_meta?.applied && (
              <TwoPassBadge meta={analysisResult.analysis._two_pass_meta} language={language} />
            )}

            {/* Action Buttons — GLOW aesthetic (Iter. 411.ii) */}
            <div className="flex gap-3">
              <button
                onClick={onSaveAsFavorite}
                data-testid="save-favorite-btn"
                className="flex-1 h-10 rounded-md flex items-center justify-center gap-2 text-sm font-black uppercase tracking-widest transition-all active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(236,72,153,0.22) 100%)',
                  border: '1px solid rgba(236,72,153,0.55)',
                  boxShadow: '0 0 12px rgba(236,72,153,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
                  color: '#fbcfe8',
                  textShadow: '0 0 5px rgba(236,72,153,0.55)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <Heart className="w-4 h-4" style={{ filter: 'drop-shadow(0 0 5px rgba(236,72,153,0.85))' }} />
                {t('meal.add_favorite', 'Ajouter aux favoris')}
              </button>
              <button
                onClick={onClose}
                className="flex-1 h-10 rounded-md flex items-center justify-center gap-2 text-sm font-black uppercase tracking-widest transition-all active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(15,15,15,0.55) 45%, rgba(148,163,184,0.18) 100%)',
                  border: '1px solid rgba(148,163,184,0.45)',
                  boxShadow: '0 0 10px rgba(148,163,184,0.28), inset 0 1px 0 rgba(255,255,255,0.05)',
                  color: '#e2e8f0',
                  textShadow: '0 0 4px rgba(148,163,184,0.5)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <X className="w-4 h-4" style={{ filter: 'drop-shadow(0 0 4px rgba(148,163,184,0.7))' }} />
                {t('common.close', 'Fermer')}
              </button>
            </div>
          </div>
            );
          })()
        )}
      </DialogContent>
    </Dialog>

    {/* 🦸 Brief hero pop-in when the meal is OMS-compliant. Auto-hides after 1.8s. */}
    <HeroMaleCelebrate
      show={heroCelebrate}
      language={language}
      onDone={() => setHeroCelebrate(false)}
    />
    </>
  );
};

export default MealAnalysisModal;
